import { useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { isPlaceholderValue, isRejectedUserValue, sanitizeFieldValue } from "@/utils/sanitizeValue";
import { canonicalAttributeValue, displayAttributeValue } from "@/utils/attributeValue";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type {
  ProposedChange,
  ValidationRule,
  WidgetData,
  WidgetField,
  WidgetFieldSource,
  WidgetGroup,
} from "@/domains/buildings/types/buildingWidget";
import { rejectionStillApplies } from "./rejectionState";
import { useRealtimeInvalidate } from "@/hooks/realtime/useRealtimeInvalidate";
import { useRevalidateOnFocus } from "@/hooks/useRevalidateOnFocus";
import { realtimeTopic } from "@/lib/realtime/channel";

/* ---------------------------------------------------------------- *
 *  Internal row shapes — kept narrow so the hook stays readable.   *
 * ---------------------------------------------------------------- */

interface ModelRow {
  attribute_nm: string;
  name_en: string | null;
  name_et: string | null;
  default_unit: string | null;
  value_type: string | null;
  attribute_type_en: string | null;
  attribute_type_et: string | null;
  sort_order: number | null;
}

interface FindingRow {
  attribute_nm: string;
  extracted_value: string | null;
  confidence: string | null;
  unit: string | null;
  default_unit: string | null;
  source_filenames: string[] | null;
  source_document_ids: number[] | null;
  modified_at: string | null;
  created_at: string | null;
  reasoning: string | null;
}

interface ApiRow {
  attribute_key: string;
  attribute_value: string | null;
  source_system: string | null;
  /** Original pre-mapping value + reasoning for an enum normalisation (WHA-3666). */
  source_value: string | null;
  reasoning: string | null;
  created_at: string | null;
  modified_at: string | null;
}

interface UserRow {
  attribute_key: string;
  attribute_value: string | null;
  edited_by?: number | null;
  edited_by_name?: string | null;
  edited_by_avatar_url?: string | null;
  edited_by_color?: string | null;
  created_at?: string | null;
  modified_at?: string | null;
}

interface IfcRow {
  attribute_nm: string;
  extracted_value: string | null;
  unit: string | null;
  confidence: string | null;
  reasoning: string | null;
  ifc_filename: string | null;
  ifc_document_id: number | null;
  modified_at: string | null;
  created_at: string | null;
}

/** Title-case a lowercase identifier: `structural` → "Structural". */
function titleCase(id: string): string {
  if (!id) return "Other";
  return id
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

type ExtractorConfidence = Extract<WidgetFieldSource, { kind: "extractor" }>["confidence"];

function normalizeConfidence(raw: string | null | undefined): ExtractorConfidence {
  const lower = (raw ?? "").toLowerCase();
  if (lower === "high" || lower === "medium" || lower === "low") return lower;
  return "not_found";
}

/**
 * Lenient equality key used to compare values across sources. "No" matches
 * "no", "  120  " matches "120", "TP1" matches "tp1", and numbers compare by
 * value — "9850" matches "9850.0". See `canonicalAttributeValue`.
 */
export function normalizeAttributeValue(v: string): string {
  return canonicalAttributeValue(v);
}

/**
 * True when the sources disagree on the value. Compared canonically: "9850"
 * from a PDF and "9850.0" from an IFC model are one value with two sources,
 * not a conflict. Exported for unit testing.
 */
export function hasValueConflict(sources: WidgetFieldSource[]): boolean {
  return new Set(sources.map((s) => normalizeAttributeValue(s.value))).size > 1;
}

const QUANTITY_VALUE_TYPES = new Set(["number", "integer", "decimal", "percentage", "currency"]);

/** A machine source's sanitized value, formatted only for numeric attributes. */
function machineValue(raw: string | null | undefined, valueType: string | null): string {
  const value = sanitizeFieldValue(raw);
  return value && valueType && QUANTITY_VALUE_TYPES.has(valueType.toLowerCase().trim())
    ? displayAttributeValue(value)
    : value;
}

/**
 * Decide whether a confirmed-state row has a newer extractor finding that
 * disagrees with the user's saved value. Multiple findings sharing one
 * proposed value collapse together. Returns null when nothing qualifies.
 *
 * Exported for unit testing (WHA-3291).
 */
export function deriveProposedChange(
  userValue: string,
  userCreatedAt: string,
  extractorEntries: Array<Extract<WidgetFieldSource, { kind: "extractor" }>>
): ProposedChange | null {
  if (!userCreatedAt) return null;
  const userNorm = normalizeAttributeValue(userValue);
  // Keep extractor entries that postdate confirmation AND disagree with it.
  // Entries with an empty createdAt are skipped — a missing timestamp can't
  // prove the finding is newer than the user's edit.
  const candidates = extractorEntries.filter(
    (e) =>
      !!e.createdAt && e.createdAt > userCreatedAt && normalizeAttributeValue(e.value) !== userNorm
  );
  if (candidates.length === 0) return null;

  // Collapse multiple findings agreeing on the same proposed value into one
  // ProposedChange whose `filenames` enumerates every contributing source.
  // The proposal we surface is the one whose latest contributing finding is
  // the freshest — stable across re-runs that just append confirmations.
  const byNormValue = new Map<string, ProposedChange>();
  for (const e of candidates) {
    const key = normalizeAttributeValue(e.value);
    const existing = byNormValue.get(key);
    if (!existing) {
      byNormValue.set(key, {
        value: e.value,
        createdAt: e.createdAt,
        filenames: [e.filename],
        reasoning: e.reasoning,
      });
    } else {
      if (!existing.filenames.includes(e.filename)) existing.filenames.push(e.filename);
      if (e.createdAt > existing.createdAt) existing.createdAt = e.createdAt;
      if (!existing.reasoning && e.reasoning) existing.reasoning = e.reasoning;
    }
  }
  return Array.from(byNormValue.values()).sort((a, b) =>
    a.createdAt > b.createdAt ? -1 : a.createdAt < b.createdAt ? 1 : 0
  )[0];
}

/* ---------------------------------------------------------------- *
 *  React Query hook.                                               *
 * ---------------------------------------------------------------- */

export const WIDGET_QUERY_KEY_PREFIX = "building-widget-data" as const;

/**
 * Window event dispatched by the chatbot store after a successful
 * `update_building_data` tool call. Listened to by `useBuildingWidgetData`
 * so the sidebar refreshes without a manual reload (WHA-3058).
 */
export const BUILDING_DATA_UPDATED_EVENT = "franklin:building-data-updated";

export function useBuildingWidgetData(buildingId: string | undefined, orgId: string | undefined) {
  const { language } = useLanguage();
  const queryClient = useQueryClient();

  const enabled = Boolean(buildingId) && Boolean(orgId);

  // Prefix invalidate: the key carries `language`, so both locale-keyed cache
  // entries have to go.
  const invalidateWidgetData = useCallback(() => {
    if (!buildingId || !orgId) return;
    void queryClient.invalidateQueries({
      queryKey: [WIDGET_QUERY_KEY_PREFIX, buildingId, orgId],
    });
  }, [buildingId, orgId, queryClient]);

  // The chatbot writes as `postgres` server-side, so it announces its own
  // writes rather than relying on realtime catching them (WHA-3058).
  useEffect(() => {
    if (!enabled) return;
    window.addEventListener(BUILDING_DATA_UPDATED_EVENT, invalidateWidgetData);
    return () => window.removeEventListener(BUILDING_DATA_UPDATED_EVENT, invalidateWidgetData);
  }, [enabled, invalidateWidgetData]);

  // Everything else that writes `building_data` announced nothing at all.
  // Extraction runs server-side over several minutes, so the page sat on "no
  // building data yet" while rows were landing, until the user reloaded.
  //
  // Subscribed here rather than in a page, because both the Building Data page
  // and the dashboard completeness widget read this hook; a subscription in
  // either one would leave the other stale.
  useRealtimeInvalidate({
    enabled,
    topic: realtimeTopic("building-data", buildingId),
    bindings: [
      { event: "*", table: "building_data", filter: `building_id=eq.${Number(buildingId)}` },
    ],
    onChange: invalidateWidgetData,
    // Extraction writes rows in a tight loop; this queryFn fans out into six
    // parallel queries, so it is worth a wider window than the documents one.
    windowMs: 1_500,
  });

  // A tab that was backgrounded during an extraction had its heartbeat
  // throttled and its socket dropped, and postgres_changes never replays.
  useRevalidateOnFocus(invalidateWidgetData, { enabled });

  return useQuery<WidgetData>({
    queryKey: [WIDGET_QUERY_KEY_PREFIX, buildingId, orgId, language],
    queryFn: async (): Promise<WidgetData> => {
      if (!buildingId || !orgId) {
        return {
          groups: [],
          pendingCount: 0,
          confirmedCount: 0,
          emptyCount: 0,
          totalCount: 0,
          lastUpdated: null,
        };
      }
      const buildingIdNum = parseInt(buildingId, 10);

      // 1. Attribute model + validation rules. Served from the `data` schema.
      const dataSchema = supabase.schema("data" as never) as unknown as typeof supabase;
      const [modelRes, validationRes, findingsRes, apiRes, userRes, ifcRes] = await Promise.all([
        dataSchema
          .from("def_building_data_model" as never)
          .select(
            "attribute_nm,name_en,name_et,default_unit,value_type,attribute_type_en,attribute_type_et,sort_order"
          )
          .eq("display_on_widget" as never, true as never)
          .order("attribute_type_en" as never, { ascending: true } as never)
          .order("sort_order" as never, { ascending: true, nullsFirst: false } as never),
        dataSchema
          .from("def_attribute_validation" as never)
          .select("attribute_nm,validation_type,validation_value,validation_value_et,sort_order"),
        supabase.rpc("get_building_findings", {
          p_org_id: orgId,
          p_building_id: buildingIdNum,
        }),
        // WHA-2944: routed through the SECURITY DEFINER wrapper because
        // PostgREST only exposes `public`; a direct .from("api_building_data")
        // hits the non-existent public.api_building_data and silently errors.
        supabase.rpc(
          "get_api_building_data" as never,
          {
            p_org_id: orgId,
            p_building_id: buildingIdNum,
          } as never
        ),
        supabase.rpc("get_user_data" as never, {
          p_org_id: orgId,
          p_building_id: buildingIdNum,
        }),
        // IFC (BIM-model) deterministic findings — data.ifc_attribute_findings
        // via a SECURITY DEFINER wrapper (migration 20260602082703, WHA-3561).
        supabase.rpc("get_ifc_findings" as never, {
          p_org_id: orgId,
          p_building_id: buildingIdNum,
        }),
      ]);

      const queryResults = [
        ["model", modelRes],
        ["validation", validationRes],
        ["findings", findingsRes],
        ["api", apiRes],
        ["user", userRes],
        ["ifc", ifcRes],
      ] as const;
      for (const [label, res] of queryResults) {
        if (res.error) console.warn(`[widget-data] ${label} query failed`, res.error);
      }
      // The model defines the page and user/findings rows define its effective
      // review state. Validation, EHR, and IFC enrich that core and may degrade
      // independently without blanking every otherwise usable attribute.
      const requiredResults = [modelRes, findingsRes, userRes] as const;
      const requiredError = requiredResults.find((result) => result.error)?.error;
      if (requiredError) throw requiredError;

      const modelRows = (modelRes.data as unknown as ModelRow[]) ?? [];
      const validationRows =
        (validationRes.data as unknown as Array<ValidationRule & { attribute_nm: string }>) ?? [];
      const findings = (findingsRes.data as unknown as FindingRow[]) ?? [];
      const apiRows = (apiRes.data as unknown as ApiRow[]) ?? [];
      const userRows = (userRes.data as unknown as UserRow[]) ?? [];
      const ifcRows = (ifcRes.data as unknown as IfcRow[]) ?? [];

      // Group validation rules by attribute_nm.
      const validationByAttr = new Map<string, ValidationRule[]>();
      for (const v of validationRows) {
        const bucket = validationByAttr.get(v.attribute_nm) ?? [];
        bucket.push({
          validation_type: v.validation_type,
          validation_value: v.validation_value,
          validation_value_et: v.validation_value_et ?? null,
          sort_order: v.sort_order,
        });
        validationByAttr.set(v.attribute_nm, bucket);
      }

      // Index by attribute key. Only the user table is single-entry-per-key
      // (get_user_data already returns the latest row); api + findings can
      // contribute multiple entries per attribute.
      const userByKey = new Map<string, UserRow>();
      for (const u of userRows) {
        // SQL NULL is an explicit rejection; the legacy preview marker remains
        // readable during rollout. Historic blank/placeholder clears carry no
        // user override and must reveal the machine value again.
        const isRejection = isRejectedUserValue(u.attribute_value);
        if (!isRejection && isPlaceholderValue(u.attribute_value)) {
          continue;
        }
        userByKey.set(u.attribute_key, u);
      }
      const apiByKey = new Map<string, ApiRow[]>();
      for (const a of apiRows) {
        if (isPlaceholderValue(a.attribute_value)) continue;
        const bucket = apiByKey.get(a.attribute_key) ?? [];
        bucket.push(a);
        apiByKey.set(a.attribute_key, bucket);
      }
      const findingsByAttr = new Map<string, FindingRow[]>();
      for (const f of findings) {
        if (isPlaceholderValue(f.extracted_value)) continue;
        const bucket = findingsByAttr.get(f.attribute_nm) ?? [];
        bucket.push(f);
        findingsByAttr.set(f.attribute_nm, bucket);
      }
      // IFC findings — one row per (ifc_document_id, attribute). Like api +
      // findings, an attribute can have several entries (one per .ifc file).
      const ifcByAttr = new Map<string, IfcRow[]>();
      for (const f of ifcRows) {
        if (isPlaceholderValue(f.extracted_value)) continue;
        const bucket = ifcByAttr.get(f.attribute_nm) ?? [];
        bucket.push(f);
        ifcByAttr.set(f.attribute_nm, bucket);
      }

      const groupMap = new Map<string, WidgetGroup>();
      let pendingCount = 0;
      let confirmedCount = 0;
      let emptyCount = 0;
      let lastUpdated: string | null = null;

      const bumpLastUpdated = (iso: string | null | undefined) => {
        if (!iso) return;
        if (!lastUpdated || iso > lastUpdated) lastUpdated = iso;
      };

      for (const m of modelRows) {
        const attrKey = m.attribute_nm;
        const sources: WidgetFieldSource[] = [];

        // --- user entry (at most one) ---
        const userRow = userByKey.get(attrKey);
        const rejectedRow = !!userRow && isRejectedUserValue(userRow.attribute_value);
        const rejectedAt = rejectedRow ? (userRow.modified_at ?? userRow.created_at ?? null) : null;
        const userValue =
          userRow && !rejectedRow ? sanitizeFieldValue(userRow.attribute_value) : "";
        const userEntryValue = userValue || null;

        // --- ehr entries (one per distinct api_building_data row) ---
        const ehrEntries: Array<Extract<WidgetFieldSource, { kind: "ehr" }>> = [];
        for (const a of apiByKey.get(attrKey) ?? []) {
          const val = machineValue(a.attribute_value, m.value_type);
          if (!val) continue;
          ehrEntries.push({
            kind: "ehr",
            value: val,
            sourceSystem: a.source_system ?? "",
            createdAt: a.created_at ?? null,
            modifiedAt: a.modified_at ?? null,
            reasoning: a.reasoning?.trim() ? a.reasoning : null,
          });
        }

        // --- extractor entries (one per source_filenames[i]) ---
        const extractorEntries: Array<Extract<WidgetFieldSource, { kind: "extractor" }>> = [];
        for (const f of findingsByAttr.get(attrKey) ?? []) {
          const val = machineValue(f.extracted_value, m.value_type);
          if (!val) continue;
          const filenames = (f.source_filenames ?? []).filter((s): s is string => !!s);
          const docIds = f.source_document_ids ?? [];
          const confidence = normalizeConfidence(f.confidence);
          if (confidence === "not_found") continue;
          const created = f.modified_at ?? f.created_at ?? "";
          if (filenames.length === 0) continue;
          const reasoning = f.reasoning?.trim() ? f.reasoning : null;
          for (let i = 0; i < filenames.length; i += 1) {
            const filename = filenames[i];
            const documentId = typeof docIds[i] === "number" ? docIds[i] : null;
            extractorEntries.push({
              kind: "extractor",
              value: val,
              filename,
              documentId,
              confidence,
              createdAt: created,
              reasoning,
            });
          }
        }

        // --- ifc entries (one per source .ifc document) ---
        // WHA-3561: a peer of ehr/extractor in the sidebar — only user edits
        // confirm a field, so IFC contributes to the pending pool and conflict
        // detection without outranking the other machine sources.
        const ifcEntries: Array<Extract<WidgetFieldSource, { kind: "ifc" }>> = [];
        for (const f of ifcByAttr.get(attrKey) ?? []) {
          const val = machineValue(f.extracted_value, m.value_type);
          if (!val) continue;
          const filename = f.ifc_filename ?? "";
          if (!filename) continue;
          ifcEntries.push({
            kind: "ifc",
            value: val,
            filename,
            documentId: typeof f.ifc_document_id === "number" ? f.ifc_document_id : null,
            confidence: normalizeConfidence(f.confidence),
            createdAt: f.modified_at ?? f.created_at ?? "",
            reasoning: f.reasoning?.trim() ? f.reasoning : null,
          });
        }

        // --- derive user entry (needs knowledge of ehr/extractor/ifc values) ---
        if (userEntryValue) {
          const userNorm = normalizeAttributeValue(userEntryValue);
          const otherNorms = new Set<string>([
            ...ehrEntries.map((e) => normalizeAttributeValue(e.value)),
            ...extractorEntries.map((e) => normalizeAttributeValue(e.value)),
            ...ifcEntries.map((e) => normalizeAttributeValue(e.value)),
          ]);
          sources.push({
            kind: "user",
            value: userEntryValue,
            editedByName: userRow?.edited_by_name ?? null,
            editedById: userRow?.edited_by ?? null,
            editedByAvatarUrl: userRow?.edited_by_avatar_url ?? null,
            editedByColor: userRow?.edited_by_color ?? null,
            createdAt: userRow?.created_at ?? userRow?.modified_at ?? "",
            confirmedBySource: otherNorms.has(userNorm),
          });
        }
        // Preserve source order: user → ehr → extractor → ifc.
        sources.push(...ehrEntries, ...extractorEntries, ...ifcEntries);

        // A rejection applies only to machine findings that existed when the
        // user rejected them. A later EHR, extractor, or IFC result re-opens the
        // field for review instead of leaving it permanently suppressed.
        const machineUpdatedAts = [
          ...ehrEntries.map((entry) => entry.createdAt),
          ...extractorEntries.map((entry) => entry.createdAt),
          ...ifcEntries.map((entry) => entry.createdAt),
        ];
        const explicitlyCleared =
          rejectedRow && rejectionStillApplies(rejectedAt, machineUpdatedAts);

        // --- state + winning value ---
        let state: WidgetField["state"];
        let value = "";
        if (explicitlyCleared || sources.length === 0) {
          state = "empty";
        } else if (userEntryValue) {
          state = "confirmed";
          value = userEntryValue;
        } else {
          state = "pending";
          value = ehrEntries[0]?.value ?? extractorEntries[0]?.value ?? ifcEntries[0]?.value ?? "";
        }

        const hasConflict = state === "pending" && hasValueConflict(sources);

        // WHA-3291: surface re-confirmation prompts when a newer extractor
        // finding disagrees with the user's saved value.
        let proposedChange: ProposedChange | null = null;
        if (state === "confirmed" && userEntryValue && userRow?.created_at) {
          proposedChange = deriveProposedChange(
            userEntryValue,
            userRow.created_at,
            extractorEntries
          );
        }

        const nameEn = m.name_en?.trim() ?? "";
        const nameEt = m.name_et?.trim() ?? "";
        const localizedLabel = language === "et" ? nameEt || nameEn : nameEn || nameEt;

        const field: WidgetField = {
          id: attrKey,
          label: localizedLabel || attrKey,
          value,
          state,
          hasConflict,
          unit: m.default_unit ?? null,
          validation: validationByAttr.get(attrKey),
          sources,
          proposedChange,
        };

        // Bump lastUpdated from any contributing row on this attribute.
        if (userRow) bumpLastUpdated(userRow.modified_at ?? userRow.created_at ?? null);
        for (const e of ehrEntries) bumpLastUpdated(e.modifiedAt);
        for (const e of extractorEntries) bumpLastUpdated(e.createdAt || null);
        for (const e of ifcEntries) bumpLastUpdated(e.createdAt || null);

        if (state === "pending") pendingCount += 1;
        if (state === "confirmed") confirmedCount += 1;
        if (state === "empty") emptyCount += 1;

        // Keep the group id stable across languages so openGroupIds
        // persistence / query caching survive a locale switch.
        const typeKey = (m.attribute_type_en ?? "other").toLowerCase();
        const typeEn = m.attribute_type_en?.trim() ?? "";
        const typeEt = m.attribute_type_et?.trim() ?? "";
        let title: string;
        if (language === "et") {
          // Estonian seed values are already capitalised — don't title-case.
          title = typeEt || typeEn || titleCase(typeKey);
        } else {
          title = typeEn || typeEt || titleCase(typeKey);
        }

        const group =
          groupMap.get(typeKey) ?? ({ id: typeKey, title, fields: [] } satisfies WidgetGroup);
        group.fields.push(field);
        groupMap.set(typeKey, group);
      }

      // Cross-check against api / user modified_at as well — lastUpdated is
      // the most recent edit across any source, not just the winning one.
      for (const a of apiRows) bumpLastUpdated(a.modified_at);
      for (const u of userRows) bumpLastUpdated(u.modified_at ?? u.created_at ?? null);
      for (const f of findings) bumpLastUpdated(f.modified_at);
      for (const f of ifcRows) bumpLastUpdated(f.modified_at ?? f.created_at ?? null);

      // WHA-3072: groups appear in a curated order rather than alphabetical
      // by attribute_type_en. Keys are lowercased English type names — same
      // value as `group.id`, so ordering is stable across locale switches.
      const GROUP_ORDER: readonly string[] = [
        "general building data",
        "construction",
        "roof and facade",
        "heating, cooling, ventilation",
        "technical systems",
        "areas and parking",
        "interior finishes",
        "furnishings and equipment",
        "outdoor areas and utilities",
        "construction conditions",
      ];
      const groupOrderIndex = (id: string) => {
        const i = GROUP_ORDER.indexOf(id);
        return i === -1 ? Number.POSITIVE_INFINITY : i;
      };
      const groups = Array.from(groupMap.values()).sort(
        (a, b) => groupOrderIndex(a.id) - groupOrderIndex(b.id)
      );
      const totalCount = modelRows.length;

      return {
        groups,
        pendingCount,
        confirmedCount,
        emptyCount,
        totalCount,
        lastUpdated,
      };
    },
    enabled: !!buildingId && !!orgId,
    staleTime: 5 * 60 * 1000,
  });
}
